import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE } from "../config/api";

export default function TestAttempt() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});          // temp
  const [savedAnswers, setSavedAnswers] = useState({}); // final saved
  const [markedForReview, setMarkedForReview] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // ⏱ TIMER (30 minutes)
  const [timeLeft, setTimeLeft] = useState(30 * 60); // seconds

  const [currentIndex, setCurrentIndex] = useState(0);

  // ================= LOAD QUESTIONS =================
  useEffect(() => {
    fetch(`${API_BASE}/tests/${id}/questions`)
      .then((res) => res.json())
      .then(setQuestions)
      .catch(console.error);
  }, [id]);

  // ================= SUBMIT =================
  const submit = useCallback(
    async (auto = false) => {
      if (submitted) return;
      setSubmitted(true);

     await fetch(`${API_BASE}/tests/${id}/submit`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  credentials: "include", // ✅ MUST ADD
  body: JSON.stringify({
    testId: id,
    answers: savedAnswers,
    auto,
  }),
});


      alert(auto ? "Time up! Test auto-submitted" : "Test submitted");
      navigate("/tests");
    },
    [submitted, savedAnswers, id, navigate]
  );

  // ================= TIMER EFFECT =================
  useEffect(() => {
    if (submitted) return;

    const timer = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timer);
          submit(true); // 🚀 AUTO SUBMIT
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [submit, submitted]);

  // ================= HANDLERS =================
  const handleSelect = (qid, option) => {
    setAnswers((p) => ({ ...p, [qid]: option }));
  };

  // SAVE → only then GREEN
  const handleSave = (qid) => {
    if (answers[qid]) {
      setSavedAnswers((p) => ({ ...p, [qid]: answers[qid] }));
      setMarkedForReview((p) => {
        const c = { ...p };
        delete c[qid];
        return c;
      });
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
    }
  };

  // ================= STATUS =================
  const getStatus = (qid) => {
    if (markedForReview[qid]) return "review";
    if (savedAnswers[qid]) return "answered";
    return "notanswered";
  };

  if (!questions.length) {
    return <div className="text-white p-6">Loading...</div>;
  }

  const q = questions[currentIndex];

  // format time mm:ss
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // ================= UI =================
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      {/* LEFT */}
      <div className="flex-1 p-10 pr-80">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold tracking-wider">
            Online Assessment
          </h1>

          <div className="bg-black border border-red-600 px-4 py-2 rounded-xl font-mono text-red-400">
            ⏱ {minutes.toString().padStart(2, "0")}:
            {seconds.toString().padStart(2, "0")}
          </div>
        </div>

        {/* QUESTION CARD */}
        <div className="bg-gray-900/90 border border-gray-700 rounded-2xl p-8 shadow-2xl">
          <p className="text-lg font-semibold mb-6 leading-relaxed">
            {q.questionNo}. {q.questionText}
          </p>

          {/* MCQ */}
          {q.type === "MCQ" && (
            <div className="space-y-3">
              {["A", "B", "C", "D"].map((k) => {
                const selected = answers[q._id] === k;
                return (
                  <label
                    key={k}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer border transition-all
                      ${
                        selected
                          ? "bg-blue-600/20 border-blue-500"
                          : "bg-gray-800 border-gray-700 hover:border-gray-500"
                      }`}
                  >
                    <input
                      type="radio"
                      name={q._id}
                      checked={selected}
                      onChange={() => handleSelect(q._id, k)}
                    />
                    <span className="text-sm">{q.options?.[k]}</span>
                  </label>
                );
              })}
            </div>
          )}

          {/* ACTION BAR */}
          <div className="mt-10 flex justify-between items-center">
            <div className="space-x-3">
              <button
                onClick={() => handleSave(q._id)}
                className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded-xl font-semibold shadow-lg"
              >
                💾 Save
              </button>

              <button
                onClick={() =>
                  setMarkedForReview((p) => ({
                    ...p,
                    [q._id]: !p[q._id],
                  }))
                }
                className="bg-yellow-600 hover:bg-yellow-700 px-6 py-2 rounded-xl font-semibold"
              >
                🟡 Mark
              </button>
            </div>

            <div className="space-x-3">
              <button
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((i) => i - 1)}
                className="bg-gray-700 px-5 py-2 rounded-xl disabled:opacity-40"
              >
                ⬅ Prev
              </button>

              <button
                disabled={currentIndex === questions.length - 1}
                onClick={() => setCurrentIndex((i) => i + 1)}
                className="bg-gray-700 px-5 py-2 rounded-xl disabled:opacity-40"
              >
                Next ➡
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={() => submit(false)}
          className="mt-10 bg-red-600 hover:bg-red-700 px-10 py-3 rounded-2xl font-bold tracking-wide shadow-xl"
        >
          Submit Test
        </button>
      </div>

      {/* RIGHT PALETTE */}
      <div className="w-72 fixed right-0 top-0 h-full bg-gray-900 border-l border-gray-700 p-4">
        <h3 className="font-bold text-center mb-4">
          Question Palette
        </h3>

        <div className="grid grid-cols-5 gap-2">
          {questions.map((qq, i) => {
            const s = getStatus(qq._id);
            const c =
              s === "answered"
                ? "bg-green-600"
                : s === "review"
                ? "bg-yellow-500"
                : "bg-red-600";

            return (
              <button
                key={qq._id}
                onClick={() => setCurrentIndex(i)}
                className={`${c} w-10 h-10 rounded-lg font-bold`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        <div className="mt-6 text-sm space-y-1">
          <p>🟢 Saved</p>
          <p>🟡 Review</p>
          <p>🔴 Not Saved</p>
        </div>
      </div>
    </div>
  );
}
